import { useState } from 'react';
import { Navigate } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';

import { Endpoints } from '@/api';
import { GenericError } from '@/components/GenericError/GenericError';
import IllustrationError from '@/components/IllustrationError';
import Loader from '@/components/Loader';
import { PageLayoutCenter } from '@/components/PageLayout';
import Stack from '@/design_system/Stack';
import Tabs from '@/design_system/Tabs';
import { Tab, TabList, TabPanel } from '@/design_system/Tabs/Tabs';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import { hasPermissionOnAnyScope } from '@/models/user';
import { useCurrentOrganization, useCurrentSession } from '@/services/auth';
import { useFetch } from '@/utils/fetch';

export const Analytics = () => {
  const { t } = useLingui();

  const { currentSession } = useCurrentSession();
  const [currentOrganization] = useCurrentOrganization();

  const canViewAnalytics = hasPermissionOnAnyScope(
    currentSession,
    'view_analytics',
    currentOrganization?.id
  );

  const {
    data: { dashboards } = { dashboards: [] },
    isLoading: isAnalyticsLoading,
    isError: isAnalyticsError,
  } = useAnalytics();

  const [selectedDashboardId, setSelectedDashboardId] = useState<string | null>(null);

  if (!canViewAnalytics) {
    return <Navigate to="/" />;
  }

  const displayedDashboard =
    dashboards.find((dashboard) => dashboard.id === selectedDashboardId) ?? dashboards[0];

  return (
    <>
      <div className="header">
        <Title size="200" as="h1">
          <Trans id="analytics.title">Analytics</Trans>
        </Title>
      </div>

      <div className="container container--list">
        {isAnalyticsError && (
          <PageLayoutCenter>
            <IllustrationError />
            <GenericError />
          </PageLayoutCenter>
        )}

        {isAnalyticsLoading && (
          <PageLayoutCenter>
            <Loader style={{ height: '40px', width: '40px' }} />
            <Paragraph>
              <Trans id="_general.loading">Loading...</Trans>
            </Paragraph>
          </PageLayoutCenter>
        )}

        {!isAnalyticsLoading && !isAnalyticsError && dashboards.length === 0 && (
          <PageLayoutCenter>
            <IllustrationError />
            <Paragraph>
              <Trans id="analytics.no-dashboards">No analytics dashboards available.</Trans>
            </Paragraph>
          </PageLayoutCenter>
        )}

        {displayedDashboard && (
          <Tabs
            gap="0"
            selectedKey={displayedDashboard.id}
            onSelectionChange={(id) => setSelectedDashboardId(String(id))}
            style={{ height: '100%' }}
          >
            {dashboards.length > 1 && (
              <TabList>
                {dashboards.map((dashboard) => (
                  <Tab key={dashboard.id} id={dashboard.id}>
                    {/* eslint-disable-next-line lingui/no-unlocalized-strings */}
                    {dashboard.provider === 'metabase' && 'Metabase - '}
                    {dashboard.name}
                  </Tab>
                ))}
              </TabList>
            )}

            <TabPanel id={displayedDashboard.id} style={{ flex: 1, height: '100%' }}>
              <Stack style={{ height: '100%' }}>
                <iframe
                  title={t({ id: 'analytics.dashboard', message: 'Analytics dashboard' })}
                  src={displayedDashboard.url}
                  style={{
                    border: 'none',
                    height: '100%',
                  }}
                />
              </Stack>
            </TabPanel>
          </Tabs>
        )}
      </div>
    </>
  );
};

const useAnalytics = () => {
  const fetch = useFetch<Endpoints['GET /analytics/dashboards']>();

  return useQuery({
    queryKey: ['analytics'],
    queryFn: () => fetch('/analytics/dashboards'),
    refetchOnMount: true, // The url is time-limited
    refetchInterval: 1000 * 60 * 8, // 8 minutes
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};
